"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { api } from "@/lib/api-client";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import type { UserOTPKindType } from "@repo/database";
import { AlertTriangleIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod/v4";

const formSchema = z.object({
  code: z.string().min(6).max(6),
});

type FormValues = z.infer<typeof formSchema>;

function formatDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

export function OTPForm({
  identifier,
  kind,
  callbackUrl,
  onDone,
  onUserLoaded,
}: {
  identifier?: string;
  kind: UserOTPKindType;
  callbackUrl?: string;
  onDone?: () => void;
  onUserLoaded?: () => void;
}) {
  const t = useTranslations();
  const { user, loaded } = useCurrentUser();

  const [timeLeftToResend, setTimeLeftToResend] = useState(60);

  const { setApiErrorsToForm } = useErrorFormatter();

  const intervalRef = useRef<number | null>(null);

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(formSchema),
    defaultValues: {
      code: "",
    },
  });

  const verifyOTPMutation = api.auth.verifyOTP.useMutation();
  const resendOTPMutation = api.auth.resendOTP.useMutation();

  useEffect(() => {
    if (user && loaded) {
      onUserLoaded?.();
    }
  }, [user, loaded]);

  useEffect(() => {
    startTimer();
  }, []);

  function startTimer() {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    setTimeLeftToResend(60);

    intervalRef.current = setInterval(() => {
      setTimeLeftToResend((prev) => {
        if (prev <= 0) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }

          return 0;
        }

        return prev - 1;
      });
    }, 1000) as any;
  }

  const onSubmit: SubmitHandler<FormValues> = async ({ code }) => {
    try {
      await verifyOTPMutation.mutateAsync({
        code,
        kind,
        identifier: identifier ?? "",
      });

      onDone?.();
    } catch (err) {
      console.error(err);
      setApiErrorsToForm(err, form);
    }
  };

  const resendOTP = async () => {
    try {
      await resendOTPMutation.mutateAsync({
        kind,
        identifier: identifier ?? "",
        callbackUrl: callbackUrl ?? "/",
      });
    } catch (err) {
      console.error(err);
    } finally {
      startTimer();
    }
  };

  const onWantsToResendOTP = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (timeLeftToResend > 0) {
      return;
    }

    await resendOTP();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <div className="font-semibold">{t("saas.auth.otp.subtitle")}</div>
          <div>
            {t(
              identifier
                ? "saas.auth.otp.description.hasEmail"
                : "saas.auth.otp.description.noEmail",
              {
                email: identifier ?? "",
              },
            )}
          </div>
        </div>

        {form.formState.isSubmitted && form.formState.errors.root?.message && (
          <Alert variant="destructive">
            <AlertTriangleIcon className="size-6" />
            <AlertDescription>
              {form.formState.errors.root.message}
            </AlertDescription>
          </Alert>
        )}

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputOTP maxLength={6} {...field}>
                  <InputOTPGroup>
                    <InputOTPSlot
                      index={0}
                      className="w-14 h-14 md:h-16 md:w-16 text-3xl"
                    />
                    <InputOTPSlot
                      index={1}
                      className="w-14 h-14 md:h-16 md:w-16 text-3xl"
                    />
                    <InputOTPSlot
                      index={2}
                      className="w-14 h-14 md:h-16 md:w-16 text-3xl"
                    />
                    <InputOTPSlot
                      index={3}
                      className="w-14 h-14 md:h-16 md:w-16 text-3xl"
                    />
                    <InputOTPSlot
                      index={4}
                      className="w-14 h-14 md:h-16 md:w-16 text-3xl"
                    />
                    <InputOTPSlot
                      index={5}
                      className="w-14 h-14 md:h-16 md:w-16 text-3xl"
                    />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          className="w-full"
          type="submit"
          disabled={form.formState.isSubmitting}
        >
          {t("saas.auth.otp.form.fields.submit")}
        </Button>

        <div>
          {t.rich("saas.auth.otp.resend", {
            a: (children) => {
              if (timeLeftToResend > 0) {
                return (
                  <span className="text-gray-500">
                    {formatDuration(timeLeftToResend)}
                  </span>
                );
              }

              return (
                <Button
                  variant="ghost"
                  className="p-0 h-auto hover:bg-transparent text-base underline font-semibold"
                  onClick={onWantsToResendOTP}
                >
                  {children}
                </Button>
              );
            },
          })}
        </div>
      </form>
    </Form>
  );
}

OTPForm.displayName = "OTPForm";
